import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import SortIcon from '@mui/icons-material/Sort';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  type ChipProps,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as api from '../../services/api';
import {
  type EvalClass,
  type Granularity,
  type MarkingMetricsResponse,
  type MarkingMetricsRow,
  type MarkingMetricsSettings,
  type MarkingReadiness,
} from '../../types';
import Loading from '../Loading';
import MarkingMetricsAssignmentPanel from './MarkingMetricsAssignmentPanel';
import MarkingMetricsGlossaryDialog from './MarkingMetricsGlossaryDialog';
import MarkingMetricsGuideDialog from './MarkingMetricsGuideDialog';
import MarkingMetricsQueue, {
  ALL_CRITERIA_QUEUE_ID,
  type QueueNavItem,
  type QueueSection,
  SECTION_LABELS,
} from './MarkingMetricsQueue';
import TranscriptComparisonDialog from './TranscriptComparisonDialog';

export type { QueueSection };
export { SECTION_LABELS };

export const GRANULARITIES: Granularity[] = ['sentence', 'paragraph', 'essay'];
export const EVAL_CLASSES: EvalClass[] = ['positive', 'negative', 'both', 'no attempt'];

export const READINESS_ORDER: MarkingReadiness[] = [
  'Meets standard',
  'Ready for review',
  'Needs attention',
  'Needs improvement',
  'Insufficient evidence',
];

export const READINESS_SUBTITLES: Record<MarkingReadiness, string> = {
  'Meets standard': 'Live; meets performance thresholds',
  'Ready for review': 'Not live; meets performance thresholds',
  'Needs attention': 'Live; misses performance thresholds',
  'Needs improvement': 'Not live; misses performance thresholds',
  'Insufficient evidence': 'Evidence thresholds not met',
};

export const readinessHex: Record<MarkingReadiness, string> = {
  'Meets standard': '#2e7d32', // green
  'Ready for review': '#81c784', // light green
  'Needs attention': '#d32f2f', // red
  'Needs improvement': '#ed6c02', // orange
  'Insufficient evidence': '#9e9e9e', // grey
};

export const readinessColor = (readiness: MarkingReadiness): ChipProps['color'] => {
  switch (readiness) {
    case 'Meets standard':
    case 'Ready for review':
      return 'success';
    case 'Needs attention':
      return 'error';
    case 'Needs improvement':
      return 'warning';
    default:
      return 'default';
  }
};

export const formatPct = (value: number | null): string =>
  value === null ? '—' : `${(value * 100).toFixed(1)}%`;

export const formatDate = (value: string | null): string =>
  value
    ? new Date(value).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

export const settingsEqual = (
  left: MarkingMetricsSettings,
  right: MarkingMetricsSettings
): boolean => JSON.stringify(left) === JSON.stringify(right);

export const evidenceState = (
  row: MarkingMetricsRow,
  settings: MarkingMetricsSettings
): 'sufficient' | 'insufficient' | 'unconfigured' => {
  const threshold = settings.evidenceThresholds[row.granularity];
  if (threshold.minComparedCases === null || threshold.minDistinctTranscriptions === null) {
    return 'unconfigured';
  }
  return row.comparedCount >= threshold.minComparedCases &&
    row.distinctComparedTranscriptions >= threshold.minDistinctTranscriptions
    ? 'sufficient'
    : 'insufficient';
};

export const readinessFor = (
  row: MarkingMetricsRow,
  settings: MarkingMetricsSettings
): MarkingReadiness => {
  if (evidenceState(row, settings) !== 'sufficient') return 'Insufficient evidence';
  const passes =
    row.exactAgreement !== null &&
    row.macroF1 !== null &&
    row.exactAgreement >= settings.exactAgreementThreshold &&
    row.macroF1 >= settings.macroF1Threshold;
  if (row.liveState === 'live') return passes ? 'Meets standard' : 'Needs attention';
  return passes ? 'Ready for review' : 'Needs improvement';
};

export const evidenceGapLabel = (
  row: MarkingMetricsRow,
  settings: MarkingMetricsSettings
): string => {
  const threshold = settings.evidenceThresholds[row.granularity];
  if (threshold.minComparedCases === null || threshold.minDistinctTranscriptions === null) {
    return 'Threshold not configured';
  }
  const caseGap = Math.max(0, threshold.minComparedCases - row.comparedCount);
  const transcriptionGap = Math.max(
    0,
    threshold.minDistinctTranscriptions - row.distinctComparedTranscriptions
  );
  if (caseGap === 0 && transcriptionGap === 0) return 'Evidence sufficient';
  const gaps: string[] = [];
  if (caseGap > 0) gaps.push(`${caseGap} ${caseGap === 1 ? 'case' : 'cases'}`);
  if (transcriptionGap > 0) {
    gaps.push(`${transcriptionGap} ${transcriptionGap === 1 ? 'transcription' : 'transcriptions'}`);
  }
  return `Needs ${gaps.join(' · ')}`;
};

export const unmarkedCases = (row: MarkingMetricsRow): number =>
  (row.evaluationCoverage?.missingEval?.cases ?? 0) +
  (row.evaluationCoverage?.outdatedEval?.cases ?? 0) +
  (row.evaluationCoverage?.invalidEval?.cases ?? 0);

export type ActionKind = 'label' | 'eval' | 'review' | 'golive' | 'none';

export interface NextAction {
  kind: ActionKind;
  label: string;
  detail: string;
}

export const clearedForGoLive = (row: MarkingMetricsRow): boolean => {
  if (row.unresolvedDisagreementCount > 0) return false;
  if (row.exactAgreement === 1) return true;
  return row.disagreementReviewRoundCount > 0;
};

export const nextAction = (
  row: MarkingMetricsRow,
  settings: MarkingMetricsSettings
): NextAction => {
  const readiness = readinessFor(row, settings);
  if (readiness === 'Insufficient evidence') {
    const threshold = settings.evidenceThresholds[row.granularity];
    const gtEnough =
      threshold.minComparedCases !== null &&
      threshold.minComparedCases !== undefined &&
      row.availableGroundTruth >= threshold.minComparedCases;
    const unmarked = unmarkedCases(row);
    if (gtEnough && unmarked > 0) {
      return {
        kind: 'eval',
        label: 'Run eval',
        detail: `${unmarked} cases without current AI marks`,
      };
    }
    return { kind: 'label', label: 'Assign labelling', detail: evidenceGapLabel(row, settings) };
  }
  if (readiness === 'Needs attention' || readiness === 'Needs improvement') {
    return {
      kind: 'review',
      label: 'Review disagreements',
      detail: `Exact ${formatPct(row.exactAgreement)} · F1 ${formatPct(row.macroF1)} vs ≥${formatPct(
        settings.exactAgreementThreshold
      )} / ≥${formatPct(settings.macroF1Threshold)}`,
    };
  }
  if (readiness === 'Ready for review') {
    if (!clearedForGoLive(row)) {
      const unresolved = row.unresolvedDisagreementCount;
      return {
        kind: 'review',
        label: 'Review disagreements',
        detail:
          unresolved > 0
            ? `${unresolved} unresolved disagreement${unresolved === 1 ? '' : 's'}`
            : 'No disagreement review yet',
      };
    }
    return { kind: 'golive', label: 'Consider going live', detail: 'Meets thresholds; not live' };
  }
  return { kind: 'none', label: '—', detail: 'Nothing to do' };
};

export const actionColor: Record<
  ActionKind,
  'primary' | 'secondary' | 'warning' | 'info' | 'inherit'
> = {
  label: 'primary',
  eval: 'secondary',
  review: 'warning',
  golive: 'info',
  none: 'inherit',
};

export const priorityRank = (row: MarkingMetricsRow, settings: MarkingMetricsSettings): number => {
  const readiness = readinessFor(row, settings);
  const live = row.liveState === 'live';
  const action = nextAction(row, settings);
  if (live) {
    if (readiness === 'Meets standard') return 0;
    if (readiness === 'Needs attention') return 1;
    if (action.kind === 'eval') return 2;
    return 3; // needs labelling
  }
  if (readiness === 'Ready for review') return 4;
  if (readiness === 'Needs improvement') return 5;
  if (action.kind === 'eval') return 6;
  return 7; // needs labelling
};

export const UNJUDGED_ELIGIBILITY_TOOLTIP =
  'Unjudged Policy Versions can exclude Labels from Eval Ground Truth until a human decides they are eligible.';

export const hasUnjudgedEligibility = (row: MarkingMetricsRow): boolean =>
  row.unjudgedPolicyVersionCount > 0;

export const hasAiMarks = (row: MarkingMetricsRow): boolean => row.comparedCount > 0;

export type MetricsSortColumn =
  | 'criterion'
  | 'evidence'
  | 'performance'
  | 'action'
  | 'cases'
  | 'transcriptions'
  | 'assignments'
  | 'disagreementReviews'
  | 'unresolvedDisagreements'
  | 'exactAgreement'
  | 'macroF1';
export type MetricsSortOrder = 'asc' | 'desc';

export interface MetricsSortOption {
  id: Exclude<MetricsSortColumn, 'evidence' | 'performance' | 'action'>;
  label: string;
  defaultOrder: MetricsSortOrder;
}

export const METRICS_SORT_OPTIONS: MetricsSortOption[] = [
  { id: 'unresolvedDisagreements', label: 'Unresolved disagreements', defaultOrder: 'desc' },
  { id: 'exactAgreement', label: 'Exact agreement', defaultOrder: 'desc' },
  { id: 'macroF1', label: 'Macro F1', defaultOrder: 'desc' },
  { id: 'assignments', label: 'Assignment count', defaultOrder: 'desc' },
  { id: 'disagreementReviews', label: 'Disagreement review count', defaultOrder: 'desc' },
  { id: 'cases', label: 'Cases', defaultOrder: 'desc' },
  { id: 'transcriptions', label: 'Transcriptions', defaultOrder: 'desc' },
  { id: 'criterion', label: 'Criterion name', defaultOrder: 'asc' },
];

const ACTION_SORT_RANK: Record<ActionKind, number> = {
  review: 0,
  eval: 1,
  label: 2,
  golive: 3,
  none: 4,
};

const compareNullableNumber = (left: number | null, right: number | null): number => {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return left - right;
};

export const compareMetricsRows = (
  left: MarkingMetricsRow,
  right: MarkingMetricsRow,
  settings: MarkingMetricsSettings,
  column: MetricsSortColumn | null,
  order: MetricsSortOrder
): number => {
  const direction = order === 'desc' ? -1 : 1;
  let result = 0;
  if (column === 'criterion') {
    result = left.name.localeCompare(right.name);
  } else if (column === 'evidence') {
    result =
      left.comparedCount - right.comparedCount ||
      left.distinctComparedTranscriptions - right.distinctComparedTranscriptions ||
      left.availableGroundTruth - right.availableGroundTruth;
  } else if (column === 'cases') {
    result = left.comparedCount - right.comparedCount;
  } else if (column === 'transcriptions') {
    result = left.distinctComparedTranscriptions - right.distinctComparedTranscriptions;
  } else if (column === 'assignments') {
    result = left.assignmentCount - right.assignmentCount;
  } else if (column === 'disagreementReviews') {
    result = left.disagreementReviewRoundCount - right.disagreementReviewRoundCount;
  } else if (column === 'unresolvedDisagreements') {
    result = left.unresolvedDisagreementCount - right.unresolvedDisagreementCount;
  } else if (column === 'exactAgreement' || column === 'macroF1') {
    const [primary, secondary] =
      column === 'exactAgreement'
        ? (['exactAgreement', 'macroF1'] as const)
        : (['macroF1', 'exactAgreement'] as const);
    if ((left[primary] === null) !== (right[primary] === null)) {
      return left[primary] === null ? 1 : -1;
    }
    result =
      compareNullableNumber(left[primary], right[primary]) ||
      compareNullableNumber(left[secondary], right[secondary]);
  } else if (column === 'performance') {
    result =
      READINESS_ORDER.indexOf(readinessFor(left, settings)) -
        READINESS_ORDER.indexOf(readinessFor(right, settings)) ||
      compareNullableNumber(left.exactAgreement, right.exactAgreement) ||
      compareNullableNumber(left.macroF1, right.macroF1);
  } else if (column === 'action') {
    const leftAction = nextAction(left, settings);
    const rightAction = nextAction(right, settings);
    result =
      ACTION_SORT_RANK[leftAction.kind] - ACTION_SORT_RANK[rightAction.kind] ||
      leftAction.label.localeCompare(rightAction.label);
  }
  if (result !== 0) return result * direction;
  return (
    priorityRank(left, settings) - priorityRank(right, settings) ||
    left.name.localeCompare(right.name)
  );
};

const diagnosticColor = (
  value: number | null,
  threshold: number,
  margin: number,
  sufficient: boolean
): { backgroundColor: string; color: string } => {
  if (value === null || !sufficient) {
    return { backgroundColor: 'grey.200', color: 'text.secondary' };
  }
  const mix = (
    start: [number, number, number],
    end: [number, number, number],
    position: number
  ): string => {
    const ratio = Math.max(0, Math.min(1, position));
    const channel = (index: number) =>
      Math.round(start[index] + (end[index] - start[index]) * ratio);
    return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`;
  };
  const safeMargin = Math.max(margin, 0.001);
  const red: [number, number, number] = [239, 154, 154];
  const amber: [number, number, number] = [255, 213, 128];
  const green: [number, number, number] = [165, 214, 167];
  const deepGreen: [number, number, number] = [76, 175, 80];

  if (value >= threshold) {
    return {
      backgroundColor: mix(green, deepGreen, (value - threshold) / safeMargin),
      color: 'rgba(0, 0, 0, 0.87)',
    };
  }
  const amberPoint = threshold - safeMargin;
  return {
    backgroundColor:
      value >= amberPoint
        ? mix(amber, green, (value - amberPoint) / safeMargin)
        : mix(red, amber, (value - (amberPoint - safeMargin)) / safeMargin),
    color: 'rgba(0, 0, 0, 0.87)',
  };
};

export const MetricValue = ({
  value,
  threshold,
  settings,
  sufficient,
}: {
  value: number | null;
  threshold: number;
  settings: MarkingMetricsSettings;
  sufficient: boolean;
}) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      px: 0.75,
      py: 0.25,
      borderRadius: 1,
      fontVariantNumeric: 'tabular-nums',
      ...diagnosticColor(value, threshold, settings.gradientAmberMargin, sufficient),
    }}
  >
    {formatPct(value)}
  </Box>
);

export const ActionButton = ({
  row,
  settings,
  onOpenRow,
  size = 'small',
}: {
  row: MarkingMetricsRow;
  settings: MarkingMetricsSettings;
  onOpenRow: (row: MarkingMetricsRow) => void;
  size?: 'small' | 'medium';
}) => {
  const action = nextAction(row, settings);
  if (action.kind === 'none') {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  if (action.kind === 'review') {
    return (
      <Button
        size={size}
        variant="contained"
        color="warning"
        href={`/admin/disagreements/consensus-vs-ai?criteriaId=${row.id}&ai_source=eval`}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {action.label}
      </Button>
    );
  }
  if (action.kind === 'eval') {
    return (
      <Tooltip title="Running an eval from here is not available yet. Ask the marking team to run one for this criterion.">
        <span>
          <Button
            size={size}
            variant="outlined"
            color="secondary"
            disabled
            sx={{ whiteSpace: 'nowrap' }}
          >
            {action.label}
          </Button>
        </span>
      </Tooltip>
    );
  }
  return (
    <Button
      size={size}
      variant={action.kind === 'label' ? 'contained' : 'outlined'}
      color={actionColor[action.kind]}
      onClick={() => onOpenRow(row)}
      sx={{ whiteSpace: 'nowrap' }}
    >
      {action.label}
    </Button>
  );
};

export const evidenceActivityLabels = (
  row: MarkingMetricsRow
): { assignments: string; reviews: string } => ({
  assignments: `${row.assignmentCount} ${row.assignmentCount === 1 ? 'assignment' : 'assignments'}`,
  reviews: `${row.disagreementReviewRoundCount} disagreement ${
    row.disagreementReviewRoundCount === 1 ? 'review' : 'reviews'
  }${
    row.unresolvedDisagreementCount > 0 ? ` · ${row.unresolvedDisagreementCount} unresolved` : ''
  }`,
});

const EvidenceActivityLabels = ({ row }: { row: MarkingMetricsRow }) => {
  const labels = evidenceActivityLabels(row);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.25 }}>
      <Typography variant="caption" color="text.secondary">
        {labels.assignments}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {labels.reviews}
      </Typography>
    </Box>
  );
};

export const EvidenceBar = ({
  row,
  settings,
}: {
  row: MarkingMetricsRow;
  settings: MarkingMetricsSettings;
}) => {
  const threshold = settings.evidenceThresholds[row.granularity];
  const minCases = threshold?.minComparedCases ?? null;
  const minTx = threshold?.minDistinctTranscriptions ?? null;
  if (minCases === null || minTx === null) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">
          Threshold not configured
        </Typography>
        <EvidenceActivityLabels row={row} />
      </Box>
    );
  }
  const caseRatio = Math.min(1, row.comparedCount / minCases);
  const txRatio = Math.min(1, row.distinctComparedTranscriptions / minTx);
  const sufficient = evidenceState(row, settings) === 'sufficient';
  const action = nextAction(row, settings);
  const missing = sufficient
    ? `Evidence sufficient · ${row.comparedCount} cases from ${row.distinctComparedTranscriptions} transcriptions`
    : action.kind === 'eval'
      ? action.detail
      : evidenceGapLabel(row, settings);
  return (
    <Tooltip
      title={`${row.comparedCount}/${minCases} compared cases · ${row.distinctComparedTranscriptions}/${minTx} distinct transcriptions · ${row.availableGroundTruth} ground-truth cases available · ${row.assignmentCount} assignments · ${row.disagreementReviewRoundCount} disagreement reviews · ${row.unresolvedDisagreementCount} unresolved`}
    >
      <Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(caseRatio, txRatio) * 100}
          color={sufficient ? 'success' : 'inherit'}
          sx={{ height: 6, borderRadius: 3, mb: 0.5, color: 'grey.500' }}
        />
        <Typography variant="caption" color="text.secondary">
          {missing}
        </Typography>
        <EvidenceActivityLabels row={row} />
      </Box>
    </Tooltip>
  );
};

export const ReadinessBar = ({
  rows,
  settings,
}: {
  rows: MarkingMetricsRow[];
  settings: MarkingMetricsSettings;
}) => {
  const counts = useMemo(() => {
    const map = new Map<MarkingReadiness, number>();
    rows.forEach(row => {
      const readiness = readinessFor(row, settings);
      map.set(readiness, (map.get(readiness) ?? 0) + 1);
    });
    return map;
  }, [rows, settings]);
  const total = rows.length || 1;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="overline" color="text.secondary">
        {rows.length} criteria by readiness
      </Typography>
      <Box sx={{ display: 'flex', height: 28, borderRadius: 1, overflow: 'hidden', mt: 0.5 }}>
        {READINESS_ORDER.map(readiness => {
          const count = counts.get(readiness) ?? 0;
          if (count === 0) return null;
          return (
            <Tooltip key={readiness} title={`${readiness}: ${count}`}>
              <Box
                sx={{
                  flexBasis: `${Math.max((count / total) * 100, 4)}%`,
                  flexGrow: 0,
                  bgcolor: readinessHex[readiness],
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {count}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
        {READINESS_ORDER.map(readiness => {
          const count = counts.get(readiness) ?? 0;
          return (
            <Chip
              key={readiness}
              size="small"
              variant="outlined"
              color={readinessColor(readiness)}
              label={`${readiness} · ${count}`}
              disabled={count === 0}
              sx={
                readiness === 'Ready for review'
                  ? {
                      borderColor: '#81c784',
                      color: '#2e7d32',
                    }
                  : readiness === 'Meets standard'
                    ? {
                        borderColor: '#2e7d32',
                        color: '#2e7d32',
                      }
                    : undefined
              }
            />
          );
        })}
      </Box>
    </Paper>
  );
};

export interface Queue extends QueueNavItem {
  matches: (row: MarkingMetricsRow, settings: MarkingMetricsSettings) => boolean;
  figureLabel?: string;
  figure?: (row: MarkingMetricsRow, settings: MarkingMetricsSettings) => React.ReactNode;
}

export const isLive = (row: MarkingMetricsRow) => row.liveState === 'live';

const performanceFigure = (row: MarkingMetricsRow, settings: MarkingMetricsSettings) => (
  <Box sx={{ display: 'flex', gap: 0.5 }}>
    <MetricValue
      value={row.exactAgreement}
      threshold={settings.exactAgreementThreshold}
      settings={settings}
      sufficient
    />
    <MetricValue
      value={row.macroF1}
      threshold={settings.macroF1Threshold}
      settings={settings}
      sufficient
    />
  </Box>
);

export const QUEUES: Queue[] = [
  // --- Live ---------------------------------------------------------------------------------
  {
    id: 'live-meets',
    section: 'live',
    name: 'Meets standard',
    why: 'Live in production, enough evidence, and passing accuracy thresholds.',
    matches: (row, settings) => readinessFor(row, settings) === 'Meets standard',
    figureLabel: 'Exact agreement · Macro F1',
    figure: performanceFigure,
  },
  {
    id: 'live-attention',
    section: 'live',
    name: 'Under-performing',
    why: 'Live in production but missing the accuracy thresholds. Fix these first.',
    urgent: true,
    matches: (row, settings) => readinessFor(row, settings) === 'Needs attention',
    figureLabel: 'Exact agreement · Macro F1',
    figure: performanceFigure,
  },
  {
    id: 'live-eval',
    section: 'live',
    name: 'Needs eval marks',
    why: 'Live in production with enough ground truth, but missing current AI eval marks.',
    matches: (row, settings) => isLive(row) && nextAction(row, settings).kind === 'eval',
  },
  {
    id: 'live-label',
    section: 'live',
    name: 'Needs labelling',
    why: 'Live in production without enough labelled cases to judge reliability.',
    urgent: true,
    matches: (row, settings) => isLive(row) && nextAction(row, settings).kind === 'label',
  },
  // --- Not live -----------------------------------------------------------------------------
  {
    id: 'notlive-ready',
    section: 'notLive',
    name: 'Ready for expert review',
    why: 'Enough evidence, passing thresholds, and disagreements reviewed (or 100% agreement). Candidates to switch on.',
    matches: (row, settings) =>
      readinessFor(row, settings) === 'Ready for review' && clearedForGoLive(row),
    figureLabel: 'Exact agreement · Macro F1',
    figure: performanceFigure,
  },
  {
    id: 'notlive-disagreements',
    section: 'notLive',
    name: 'Needs disagreement review',
    why: 'Meets thresholds, but disagreements are still outstanding or have never been reviewed.',
    matches: (row, settings) =>
      readinessFor(row, settings) === 'Ready for review' && !clearedForGoLive(row),
    figureLabel: 'Exact agreement · Macro F1',
    figure: performanceFigure,
  },
  {
    id: 'notlive-improve',
    section: 'notLive',
    name: 'Under-performing',
    why: 'Enough evidence but missing accuracy thresholds. Review disagreements.',
    matches: (row, settings) => readinessFor(row, settings) === 'Needs improvement',
    figureLabel: 'Exact agreement · Macro F1',
    figure: performanceFigure,
  },
  {
    id: 'notlive-eval',
    section: 'notLive',
    name: 'Needs eval marks',
    why: 'Not live, enough ground truth exists, but missing current AI eval marks.',
    matches: (row, settings) => !isLive(row) && nextAction(row, settings).kind === 'eval',
  },
  {
    id: 'notlive-label',
    section: 'notLive',
    name: 'Needs labelling',
    why: 'Not live, and not enough labelled cases to judge.',
    matches: (row, settings) => !isLive(row) && nextAction(row, settings).kind === 'label',
  },
];

const ThresholdInput = ({
  label,
  value,
  onChange,
  percent = false,
  min = 0,
  helperText,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  percent?: boolean;
  min?: number;
  helperText?: string;
}) => (
  <TextField
    fullWidth
    type="number"
    size="small"
    label={label}
    value={value ?? ''}
    onChange={event => {
      const raw = event.target.value;
      onChange(raw === '' ? null : Number(raw));
    }}
    inputProps={{ min, max: percent ? 1 : undefined, step: percent ? 0.01 : 1 }}
    helperText={
      value === null
        ? 'Unconfigured'
        : [percent ? formatPct(value) : null, helperText].filter(Boolean).join(' — ') || undefined
    }
  />
);

let cachedMarkingMetrics: MarkingMetricsResponse | null = null;

export const resetMarkingMetricsCache = (): void => {
  cachedMarkingMetrics = null;
};

const MarkingMetrics = () => {
  const initialCached = process.env.NODE_ENV === 'test' ? null : cachedMarkingMetrics;
  const [data, setData] = useState<MarkingMetricsResponse | null>(() => initialCached);
  const [savedSettings, setSavedSettings] = useState<MarkingMetricsSettings | null>(
    () => initialCached?.settings ?? null
  );
  const [draftSettings, setDraftSettings] = useState<MarkingMetricsSettings | null>(
    () => initialCached?.settings ?? null
  );
  const [isLoading, setIsLoading] = useState(!initialCached);
  const [isRefreshing, setIsRefreshing] = useState(!!initialCached);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState(false);
  const [drawerRow, setDrawerRow] = useState<MarkingMetricsRow | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [selectedQueueId, setSelectedQueueId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [unjudgedOnly, setUnjudgedOnly] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [comparisonRow, setComparisonRow] = useState<MarkingMetricsRow | null>(null);
  const [sortColumn, setSortColumn] = useState<MetricsSortColumn | null>(null);
  const [sortOrder, setSortOrder] = useState<MetricsSortOrder>('asc');
  const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!cachedMarkingMetrics) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    api
      .fetchMarkingMetrics()
      .then(response => {
        if (cancelled) return;
        cachedMarkingMetrics = response;
        setData(response);
        setSavedSettings(current => current ?? response.settings);
        setDraftSettings(current => current ?? response.settings);
      })
      .catch(err => {
        console.error('Failed to load marking metrics:', err);
        if (!cancelled && !cachedMarkingMetrics) setError('Failed to load marking metrics');
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeCriteria = useMemo(() => {
    if (!data) return [];
    return data.criteria.filter(row => showArchived || !row.isArchived);
  }, [data, showArchived]);

  const counts = useMemo(() => {
    if (!data || !draftSettings) return {} as Record<string, number>;
    return Object.fromEntries(
      QUEUES.map(queue => [
        queue.id,
        activeCriteria.filter(row => queue.matches(row, draftSettings)).length,
      ])
    ) as Record<string, number>;
  }, [data, draftSettings, activeCriteria]);

  const sectionCounts = useMemo(() => {
    if (!data) return { live: 0, notLive: 0 };
    return {
      live: activeCriteria.filter(isLive).length,
      notLive: activeCriteria.filter(row => !isLive(row)).length,
    };
  }, [data, activeCriteria]);

  const unjudgedTotalCount = useMemo(() => {
    return activeCriteria.filter(hasUnjudgedEligibility).length;
  }, [activeCriteria]);

  const rowMatches = useCallback(
    (row: MarkingMetricsRow) => {
      const q = query.trim().toLowerCase();
      if (q && !row.name.toLowerCase().includes(q)) return false;
      if (unjudgedOnly && !hasUnjudgedEligibility(row)) return false;
      return true;
    },
    [query, unjudgedOnly]
  );

  const hasActiveFilter = Boolean(query.trim() || unjudgedOnly || showArchived);
  const filterMode =
    query.trim() && unjudgedOnly
      ? 'both'
      : unjudgedOnly
        ? 'unjudged'
        : query.trim()
          ? 'search'
          : undefined;

  const matchCounts = useMemo(() => {
    if (!data || !draftSettings) return {} as Record<string, number>;
    return Object.fromEntries(
      QUEUES.map(queue => [
        queue.id,
        activeCriteria.filter(row => queue.matches(row, draftSettings) && rowMatches(row)).length,
      ])
    ) as Record<string, number>;
  }, [data, draftSettings, activeCriteria, rowMatches]);

  const totalMatchCount = useMemo(() => {
    return activeCriteria.filter(rowMatches).length;
  }, [activeCriteria, rowMatches]);

  const sectionMatchCounts = useMemo(() => {
    return {
      live: activeCriteria.filter(row => isLive(row) && rowMatches(row)).length,
      notLive: activeCriteria.filter(row => !isLive(row) && rowMatches(row)).length,
    };
  }, [activeCriteria, rowMatches]);

  const queueId = useMemo(() => {
    if (selectedQueueId !== null) return selectedQueueId;
    return QUEUES.find(queue => (counts[queue.id] ?? 0) > 0)?.id ?? QUEUES[0].id;
  }, [selectedQueueId, counts]);

  const isAllCriteria = queueId === ALL_CRITERIA_QUEUE_ID;
  const queue = QUEUES.find(item => item.id === queueId);

  const queueRows = useMemo(() => {
    if (!data || !draftSettings) return [];
    let baseRows: MarkingMetricsRow[];
    if (isAllCriteria) {
      baseRows = [...activeCriteria];
    } else {
      if (!queue) return [];
      baseRows = activeCriteria.filter(row => queue.matches(row, draftSettings));
    }
    const filtered = baseRows.filter(rowMatches);
    return [...filtered].sort((left, right) =>
      compareMetricsRows(left, right, draftSettings, sortColumn, sortOrder)
    );
  }, [
    data,
    draftSettings,
    isAllCriteria,
    queue,
    activeCriteria,
    rowMatches,
    sortColumn,
    sortOrder,
  ]);

  const handleSort = (column: MetricsSortColumn) => {
    if (sortColumn === column) {
      setSortOrder(current => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortOrder('asc');
  };

  const selectedSortOption = METRICS_SORT_OPTIONS.find(option => option.id === sortColumn);

  const handleSortOption = (option: MetricsSortOption) => {
    setSortColumn(option.id);
    setSortOrder(option.defaultOrder);
    setSortMenuAnchor(null);
  };

  const showFigure = isAllCriteria || queue?.figure !== undefined;

  const modified = useMemo(
    () => Boolean(draftSettings && savedSettings && !settingsEqual(draftSettings, savedSettings)),
    [draftSettings, savedSettings]
  );

  const updateSetting = (key: keyof MarkingMetricsSettings, value: number | null) => {
    if (!draftSettings || value === null) return;
    setDraftSettings({ ...draftSettings, [key]: value });
    setSavedMessage(false);
  };

  const updateEvidence = (
    granularity: Granularity,
    field: 'minComparedCases' | 'minDistinctTranscriptions',
    value: number | null
  ) => {
    setDraftSettings(current => {
      if (!current) return current;
      return {
        ...current,
        evidenceThresholds: {
          ...current.evidenceThresholds,
          [granularity]: {
            ...current.evidenceThresholds[granularity],
            [field]: value,
          },
        },
      };
    });
    setSavedMessage(false);
  };

  const saveSettings = async () => {
    if (!draftSettings) return;
    setIsSaving(true);
    try {
      const updated = await api.updateMarkingMetricsSettings(draftSettings);
      setSavedSettings(updated);
      setDraftSettings(updated);
      if (cachedMarkingMetrics) {
        cachedMarkingMetrics = {
          ...cachedMarkingMetrics,
          settings: updated,
        };
      }
      setSavedMessage(true);
      setSettingsOpen(false);
    } catch (err) {
      console.error('Failed to save marking metrics settings:', err);
      setError('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && !data) return <Loading />;
  if (!data || !draftSettings || !savedSettings) {
    return <Alert severity="error">{error ?? 'No marking metrics were returned'}</Alert>;
  }

  const activeDrawerRow = drawerRow
    ? (data.criteria.find(c => c.id === drawerRow.id) ?? drawerRow)
    : null;

  return (
    <Container
      maxWidth={false}
      sx={{
        my: 4,
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 128px)',
      }}
    >
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        {isRefreshing && <LinearProgress sx={{ mb: 1.5, height: 3, borderRadius: 1 }} />}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            Marking metrics
          </Typography>
          {modified && (
            <Tooltip title="You are previewing unsaved threshold changes. Readiness shown here differs from what other users see.">
              <Chip
                color="warning"
                size="small"
                label="Previewing unsaved thresholds"
                onClick={() => setSettingsOpen(true)}
              />
            </Tooltip>
          )}
          <Button
            size="small"
            startIcon={<HelpOutlineIcon />}
            variant="outlined"
            onClick={() => setGuideOpen(true)}
          >
            How this works
          </Button>
          <Button
            size="small"
            startIcon={<MenuBookOutlinedIcon />}
            variant="outlined"
            onClick={() => setGlossaryOpen(true)}
          >
            Glossary
          </Button>
          <Tooltip title="Readiness thresholds">
            <IconButton aria-label="Readiness settings" onClick={() => setSettingsOpen(true)}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography color="text.secondary">
          Work through the queues on the left. Each one is a job: what needs labelling, what needs
          eval marks, what needs a disagreement review, and what is ready to go live.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}
      {savedMessage && (
        <Alert severity="success" sx={{ mb: 2, flexShrink: 0 }}>
          Threshold settings saved
        </Alert>
      )}

      <Box sx={{ flexShrink: 0 }}>
        <ReadinessBar rows={activeCriteria} settings={draftSettings} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          my: 1.5,
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder="Search criteria"
          value={query}
          onChange={event => setQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setQuery('')}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ width: 320 }}
        />

        <Tooltip
          title={
            unjudgedOnly ? 'Click to clear filter' : 'Filter criteria with unjudged Policy Versions'
          }
        >
          <Chip
            icon={<WarningAmberIcon fontSize="small" />}
            label={`Has unjudged policy (${unjudgedTotalCount})`}
            clickable
            color={unjudgedOnly ? 'warning' : 'default'}
            variant={unjudgedOnly ? 'filled' : 'outlined'}
            onClick={() => setUnjudgedOnly(prev => !prev)}
            sx={{
              fontWeight: unjudgedOnly ? 700 : 500,
              height: 38,
              px: 0.5,
              '& .MuiChip-icon': {
                color: unjudgedOnly ? 'inherit' : 'warning.main',
              },
            }}
          />
        </Tooltip>

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showArchived}
              onChange={event => setShowArchived(event.target.checked)}
            />
          }
          label="Show archived"
          sx={{ mr: 0, whiteSpace: 'nowrap' }}
        />

        {hasActiveFilter && (
          <Button
            size="small"
            color="inherit"
            onClick={() => {
              setQuery('');
              setUnjudgedOnly(false);
              setShowArchived(false);
            }}
          >
            Clear filters
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '320px 1fr',
          gap: 2,
          alignItems: 'stretch',
          flex: 1,
          minHeight: 0,
        }}
      >
        <MarkingMetricsQueue
          queues={QUEUES}
          selectedQueueId={queueId}
          counts={counts}
          sectionCounts={hasActiveFilter ? sectionMatchCounts : sectionCounts}
          totalCount={activeCriteria.length}
          onSelectQueue={setSelectedQueueId}
          matchCounts={matchCounts}
          totalMatchCount={totalMatchCount}
          hasActiveFilter={hasActiveFilter}
          filterMode={filterMode}
        />

        <Paper variant="outlined" sx={{ p: 2, overflow: 'auto', minHeight: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {isAllCriteria
                ? 'All criteria'
                : `${SECTION_LABELS[queue!.section]} · ${queue!.name}`}{' '}
              <Typography component="span" color="text.secondary">
                · {queueRows.length}
              </Typography>
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SortIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={event => setSortMenuAnchor(event.currentTarget)}
              aria-label="Sort by"
              aria-haspopup="menu"
              aria-expanded={Boolean(sortMenuAnchor)}
              aria-controls={sortMenuAnchor ? 'metrics-sort-menu' : undefined}
            >
              {selectedSortOption ? `Sort by: ${selectedSortOption.label}` : 'Sort by'}
            </Button>
            <Menu
              id="metrics-sort-menu"
              anchorEl={sortMenuAnchor}
              open={Boolean(sortMenuAnchor)}
              onClose={() => setSortMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {METRICS_SORT_OPTIONS.map(option => (
                <MenuItem
                  key={option.id}
                  selected={sortColumn === option.id}
                  onClick={() => handleSortOption(option)}
                >
                  <ListItemIcon>
                    {sortColumn === option.id ? <CheckIcon fontSize="small" /> : null}
                  </ListItemIcon>
                  <ListItemText>{option.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
            <Tooltip
              title={
                sortColumn
                  ? sortOrder === 'asc'
                    ? 'Ascending'
                    : 'Descending'
                  : 'Choose a sort field first'
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!sortColumn}
                  aria-label={sortOrder === 'asc' ? 'Sort ascending' : 'Sort descending'}
                  onClick={() => setSortOrder(current => (current === 'asc' ? 'desc' : 'asc'))}
                >
                  {sortOrder === 'asc' ? (
                    <ArrowUpwardIcon fontSize="small" />
                  ) : (
                    <ArrowDownwardIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isAllCriteria
              ? sortColumn
                ? 'Complete list of all criteria, sorted by the selected column.'
                : 'Complete list of all criteria, sorted by operational priority.'
              : queue!.why}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ width: 44, minWidth: 44, px: 1 }}
                  aria-label="Transcript comparison"
                />
                <TableCell sx={{ width: '40%' }}>
                  <TableSortLabel
                    active={sortColumn === 'criterion'}
                    direction={sortColumn === 'criterion' ? sortOrder : 'asc'}
                    onClick={() => handleSort('criterion')}
                  >
                    Criterion
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 210, minWidth: 210 }}>
                  <TableSortLabel
                    active={sortColumn === 'evidence'}
                    direction={sortColumn === 'evidence' ? sortOrder : 'asc'}
                    onClick={() => handleSort('evidence')}
                  >
                    Evidence
                  </TableSortLabel>
                </TableCell>
                {showFigure && (
                  <TableCell>
                    <TableSortLabel
                      active={sortColumn === 'performance'}
                      direction={sortColumn === 'performance' ? sortOrder : 'asc'}
                      onClick={() => handleSort('performance')}
                    >
                      {isAllCriteria ? 'Readiness · Performance' : queue!.figureLabel}
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell align="right">
                  <TableSortLabel
                    active={sortColumn === 'action'}
                    direction={sortColumn === 'action' ? sortOrder : 'asc'}
                    onClick={() => handleSort('action')}
                    sx={{ justifyContent: 'flex-end', width: '100%' }}
                  >
                    Next action
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queueRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={showFigure ? 5 : 4}>
                    <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      {hasActiveFilter
                        ? 'No criteria in this queue match the active search or filter.'
                        : 'Nothing in this queue.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {queueRows.map(row => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    ...(hasUnjudgedEligibility(row)
                      ? {
                          bgcolor: 'rgba(237, 108, 2, 0.08)',
                          '&:hover': { bgcolor: 'rgba(237, 108, 2, 0.14)' },
                        }
                      : {}),
                  }}
                  onClick={() => setDrawerRow(row)}
                >
                  <TableCell
                    sx={{ width: 44, minWidth: 44, px: 1 }}
                    align="center"
                    onClick={event => event.stopPropagation()}
                  >
                    {hasAiMarks(row) ? (
                      <Tooltip title="View transcript comparison (AI mark vs ground truth)">
                        <IconButton
                          size="small"
                          aria-label={`View transcript comparison for ${row.name}`}
                          onClick={() => setComparisonRow(row)}
                          sx={{
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'primary.50' },
                          }}
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Nothing to explore">
                        <span>
                          <IconButton
                            size="small"
                            disabled
                            aria-label={`Nothing to explore for ${row.name}`}
                          >
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ width: '40%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{row.name}</Typography>
                      {row.isArchived && <Chip size="small" variant="outlined" label="Archived" />}
                      {hasUnjudgedEligibility(row) && (
                        <Tooltip
                          title={UNJUDGED_ELIGIBILITY_TOOLTIP}
                          enterDelay={0}
                          enterTouchDelay={0}
                        >
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label="Has unjudged policy"
                          />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {row.granularity}
                      {row.liveState !== 'live' ? ` · ${row.liveState}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 210, minWidth: 210 }}>
                    <EvidenceBar row={row} settings={draftSettings} />
                  </TableCell>
                  {showFigure && (
                    <TableCell>
                      {isAllCriteria ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            size="small"
                            label={readinessFor(row, draftSettings)}
                            color={readinessColor(readinessFor(row, draftSettings))}
                            sx={
                              readinessFor(row, draftSettings) === 'Ready for review'
                                ? { bgcolor: '#81c784', color: '#1b5e20' }
                                : readinessFor(row, draftSettings) === 'Meets standard'
                                  ? { bgcolor: '#2e7d32', color: '#fff' }
                                  : undefined
                            }
                          />
                          {row.exactAgreement !== null && row.macroF1 !== null && (
                            <Typography variant="caption" color="text.secondary">
                              Exact {formatPct(row.exactAgreement)} · F1 {formatPct(row.macroF1)}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" component="div">
                          {queue?.figure?.(row, draftSettings)}
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell align="right" onClick={event => event.stopPropagation()}>
                    <ActionButton row={row} settings={draftSettings} onOpenRow={setDrawerRow} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Drawer
        anchor="right"
        open={activeDrawerRow !== null}
        onClose={() => setDrawerRow(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 760 }, p: 3 } }}
      >
        {activeDrawerRow && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h5">{activeDrawerRow.name}</Typography>
                  {activeDrawerRow.isArchived && (
                    <Chip size="small" variant="outlined" label="Archived" />
                  )}
                </Box>
                <Typography color="text.secondary">
                  {activeDrawerRow.granularity} · {activeDrawerRow.liveState}
                </Typography>
              </Box>
              <IconButton aria-label="Close details" onClick={() => setDrawerRow(null)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {hasUnjudgedEligibility(activeDrawerRow) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {activeDrawerRow.unjudgedPolicyVersionCount} unjudged Policy Version
                {activeDrawerRow.unjudgedPolicyVersionCount === 1 ? '' : 's'}
                {activeDrawerRow.unjudgedLabelCount > 0
                  ? ` · ${activeDrawerRow.unjudgedLabelCount} Labels at stake`
                  : ''}
                . Unjudged Policy Versions can exclude Labels from Eval Ground Truth.{' '}
                <Link href={`/admin/policy-comparison?criteriaId=${activeDrawerRow.id}`}>
                  Judge eligibility
                </Link>
              </Alert>
            )}

            {hasAiMarks(activeDrawerRow) ? (
              <Button
                startIcon={<SearchIcon />}
                variant="outlined"
                onClick={() => setComparisonRow(activeDrawerRow)}
                sx={{ mt: 2 }}
              >
                View transcript comparison
              </Button>
            ) : (
              <Tooltip title="Nothing to explore">
                <span>
                  <Button startIcon={<SearchIcon />} variant="outlined" disabled sx={{ mt: 2 }}>
                    View transcript comparison
                  </Button>
                </span>
              </Tooltip>
            )}

            <Paper variant="outlined" sx={{ p: 2, mt: 2.5, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Readiness breakdown
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip size="small" label={readinessFor(activeDrawerRow, draftSettings)} />
                <Typography variant="body2" color="text.secondary">
                  {READINESS_SUBTITLES[readinessFor(activeDrawerRow, draftSettings)]}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  fontSize: '0.875rem',
                }}
              >
                <Typography variant="body2">
                  <strong>Evidence:</strong> {evidenceState(activeDrawerRow, draftSettings)} (
                  {evidenceGapLabel(activeDrawerRow, draftSettings)})
                </Typography>
                <Typography variant="body2">
                  <strong>Performance:</strong>{' '}
                  {activeDrawerRow.performancePasses ? 'Passes' : 'Fails'} (Exact agreement{' '}
                  {formatPct(activeDrawerRow.exactAgreement)} vs ≥
                  {formatPct(draftSettings.exactAgreementThreshold)}, Macro F1{' '}
                  {formatPct(activeDrawerRow.macroF1)} vs ≥
                  {formatPct(draftSettings.macroF1Threshold)})
                </Typography>
                <Typography variant="body2">
                  <strong>Live state:</strong> {activeDrawerRow.liveState}
                </Typography>
              </Box>
            </Paper>

            {readinessFor(activeDrawerRow, draftSettings) === 'Insufficient evidence' && (
              <Box sx={{ mt: 2.5 }}>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  <strong>{evidenceGapLabel(activeDrawerRow, draftSettings)}</strong> before this
                  criterion has enough evidence for a reliable readiness decision.
                </Alert>
                <MarkingMetricsAssignmentPanel
                  key={activeDrawerRow.id}
                  row={activeDrawerRow}
                  settings={draftSettings}
                />
              </Box>
            )}

            {nextAction(activeDrawerRow, draftSettings).kind === 'review' && (
              <Button
                href={`/admin/disagreements/consensus-vs-ai?criteriaId=${activeDrawerRow.id}&ai_source=eval`}
                variant="contained"
                color="warning"
                sx={{ mt: 2.5 }}
              >
                Review eval disagreements · {activeDrawerRow.disagreementReviewRoundCount} review
                rounds
              </Button>
            )}

            <Typography variant="h6" sx={{ mt: 3 }}>
              Evaluation coverage
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {activeDrawerRow.availableGroundTruth} available ground-truth cases ·{' '}
              {activeDrawerRow.comparedCount} compared with current AI marks
            </Typography>
            <Typography>
              Current {activeDrawerRow.evaluationCoverage.current.cases} · Missing{' '}
              {activeDrawerRow.evaluationCoverage.missingEval.cases} · Outdated{' '}
              {activeDrawerRow.evaluationCoverage.outdatedEval.cases} · Invalid{' '}
              {activeDrawerRow.evaluationCoverage.invalidEval.cases} · Unresolved{' '}
              {activeDrawerRow.evaluationCoverage.identityUnresolved.cases}
            </Typography>
            {activeDrawerRow.evalUpdateRequired && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                AI evaluation update required: some ground-truth cases do not have current, valid AI
                marks.
              </Alert>
            )}

            <Typography variant="h6" sx={{ mt: 3 }}>
              Represented classes: {activeDrawerRow.representedClassCount} / 4
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Performance per class. Macro metrics are the equal-weight average of the{' '}
              {activeDrawerRow.scoredClassCount} class
              {activeDrawerRow.scoredClassCount === 1 ? '' : 'es'} with at least{' '}
              {draftSettings.minClassSupport} ground-truth case
              {draftSettings.minClassSupport === 1 ? '' : 's'}; smaller classes are shown but not
              counted.
            </Typography>
            {EVAL_CLASSES.map(klass => {
              const values = activeDrawerRow.perClass[klass];
              const belowSupport =
                values.support > 0 && values.support < draftSettings.minClassSupport;
              return (
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }} key={klass}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2">{klass}</Typography>
                    {belowSupport && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Not in macro average · ${values.support} of ${draftSettings.minClassSupport} cases`}
                      />
                    )}
                  </Box>
                  <Typography>
                    Precision {formatPct(values.precision)} · Recall {formatPct(values.recall)} · F1{' '}
                    {formatPct(values.f1)} · Support {values.support}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    One-vs-rest confusion: TP {values.tp} · FP {values.fp} · FN {values.fn} · TN{' '}
                    {values.tn}
                  </Typography>
                </Paper>
              );
            })}

            <Typography variant="h6" sx={{ mt: 3 }}>
              Provenance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Origin of ground-truth cases and exclusion reasons.
            </Typography>
            <Typography>
              Reviewed outputs {activeDrawerRow.groundTruthProvenance.reviewedOutputCount} ·
              Explicit labels {activeDrawerRow.groundTruthProvenance.explicitLabelCount} · Implicit
              no attempt {activeDrawerRow.groundTruthProvenance.implicitNoAttemptCount}
            </Typography>
            <Typography>
              Stale eligibility {activeDrawerRow.groundTruthProvenance.staleEligibilityCaseCount} ·
              Conflicting {activeDrawerRow.groundTruthProvenance.conflictingCount} · Unresolved
              identity {activeDrawerRow.groundTruthProvenance.unresolvedIdentityCount}
            </Typography>

            <Typography variant="h6" sx={{ mt: 3 }}>
              Eval provenance
            </Typography>
            <Typography>
              {activeDrawerRow.evalProvenance.contributingRunCount} contributing run(s)
            </Typography>
            <Typography>
              Selected eval dates: {formatDate(activeDrawerRow.evalProvenance.oldestSelectedAt)} –{' '}
              {formatDate(activeDrawerRow.evalProvenance.newestSelectedAt)}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 1,
                mt: 3,
              }}
            >
              <Link href={`/admin/policy-comparison?criteriaId=${activeDrawerRow.id}`}>
                Open policy comparison
              </Link>
            </Box>
          </Box>
        )}
      </Drawer>

      <MarkingMetricsGuideDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        settings={draftSettings}
        readinessStates={READINESS_ORDER}
        readinessSubtitles={READINESS_SUBTITLES}
      />

      <MarkingMetricsGlossaryDialog open={glossaryOpen} onClose={() => setGlossaryOpen(false)} />

      <TranscriptComparisonDialog
        open={comparisonRow !== null}
        row={comparisonRow}
        onClose={() => setComparisonRow(null)}
      />

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
              Saved readiness thresholds
            </Typography>
            {modified && <Chip size="small" color="warning" label="Modified — not saved" />}
            <IconButton aria-label="Close settings" onClick={() => setSettingsOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These thresholds decide each criterion&apos;s readiness verdict. Edits preview in the
            table immediately; nothing changes for other users until you press Save.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Performance thresholds
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            A criterion passes performance only when it meets <em>both</em> thresholds. Exact
            agreement asks how often the AI gives exactly the same mark as the human; Macro F1
            checks that every kind of mark is handled well, not just the common ones.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <ThresholdInput
                label="Exact agreement threshold"
                value={draftSettings.exactAgreementThreshold}
                percent
                helperText="Share of compared cases where the AI mark matches the human mark"
                onChange={value => updateSetting('exactAgreementThreshold', value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ThresholdInput
                label="Macro F1 threshold"
                value={draftSettings.macroF1Threshold}
                percent
                helperText="Equal-weight average of F1 across the outcome classes"
                onChange={value => updateSetting('macroF1Threshold', value)}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            How the macro averages are calculated
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Every mark is one of four outcome classes: positive, negative, both, or no attempt.
            Macro precision, recall, and F1 average the per-class scores with equal weight, so a
            class with only one or two ground-truth cases could swing the whole result on a single
            decision. A class only joins the average once it has at least this many ground-truth
            cases; smaller classes are still shown in the details drawer but marked as not counted.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <ThresholdInput
                label="Min ground-truth cases per outcome class"
                value={draftSettings.minClassSupport}
                min={1}
                helperText="Set to 1 to include every class that appears in the ground truth"
                onChange={value => updateSetting('minClassSupport', value)}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Colour coding
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Metric cells are green at or above their threshold, amber within this margin below it,
            and red further below. This only affects colours, not the readiness verdict.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <ThresholdInput
                label="Amber margin"
                value={draftSettings.gradientAmberMargin}
                percent
                helperText="e.g. 5% means a 78% score is amber against an 80% threshold"
                onChange={value => updateSetting('gradientAmberMargin', value)}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Evidence thresholds
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Before performance is judged at all, a criterion needs enough ground-truth cases with
            current AI marks, spread across enough distinct transcriptions so that one unusual piece
            of writing cannot dominate. Criteria below either minimum show as &ldquo;Insufficient
            evidence&rdquo;. Set per granularity because essay-level criteria produce one case per
            transcription while sentence-level criteria produce many.
          </Typography>
          <Grid container spacing={2}>
            {GRANULARITIES.map(granularity => (
              <Grid item xs={12} md={4} key={granularity}>
                <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', mb: 1 }}>
                  {granularity}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <ThresholdInput
                    label={`${granularity} min cases`}
                    value={draftSettings.evidenceThresholds[granularity].minComparedCases}
                    onChange={value => updateEvidence(granularity, 'minComparedCases', value)}
                  />
                  <ThresholdInput
                    label={`${granularity} min transcriptions`}
                    value={draftSettings.evidenceThresholds[granularity].minDistinctTranscriptions}
                    onChange={value =>
                      updateEvidence(granularity, 'minDistinctTranscriptions', value)
                    }
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
          {savedSettings.changedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Last saved {formatDate(savedSettings.changedAt)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            disabled={!modified || isSaving}
            onClick={() => {
              setDraftSettings(savedSettings);
              setSavedMessage(false);
            }}
          >
            Reset
          </Button>
          <Button variant="contained" disabled={!modified || isSaving} onClick={saveSettings}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MarkingMetrics;
